import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import {
  Wallet, TrendingUp, Zap, CheckCircle,
  AlertCircle, Smartphone, Clock, ArrowRight,
  X, CreditCard, RefreshCw, MessageSquare, Shield
} from 'lucide-react';
import './Billing.css';

const getPhoneFromToken = () => {
  try {
    const token = localStorage.getItem('wa_token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.phoneNumber || '';
  } catch {
    return '';
  }
};

const Billing = () => {
  const { user, checkRegistrationStatus: checkAuthStatus } = useAuth();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(getPhoneFromToken);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [registrationFee, setRegistrationFee] = useState(null);
  const [hasPaidRegistration, setHasPaidRegistration] = useState(user?.hasPaidRegistration || false);
  const [purchasing, setPurchasing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  const pollingRef = useRef(null);

  useEffect(() => {
    initPage();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);



  // fetchPricing inaitwa baada ya credits kubadilika - debounced
  useEffect(() => {
    const numCredits = parseInt(credits) || 0;
    if (numCredits > 0 && hasPaidRegistration) {
      const timer = setTimeout(() => fetchPricing(numCredits), 400);
      return () => clearTimeout(timer);
    } else {
      setPricing(null);
    }
  }, [credits, hasPaidRegistration]);

  const initPage = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBillingData(),
        fetchRegistrationFee(),
        checkRegistrationStatus(),
        fetchTransactions(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      const response = await api.get('/api/payments/dashboard/stats');
      setStats(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching billing data:', err);
    }
  };

  const fetchPricing = async (creditCount) => {
    if (!creditCount || creditCount <= 0) { setPricing(null); return; }
    setPricingLoading(true);
    try {
      const response = await api.get(`/api/payments/pricing?credits=${creditCount}`);
      setPricing(response.data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    } finally {
      setPricingLoading(false);
    }
  };

  const fetchRegistrationFee = async () => {
    try {
      const response = await api.get('/api/payments/registration-fee');
      setRegistrationFee(response.data);
    } catch (err) {
      console.error('Error fetching registration fee:', err);
    }
  };

  const fetchTransactions = async () => {
    setTxLoading(true);
    try {
      const response = await api.get('/api/payments/transactions');
      setTransactions(response.data || []);
    } catch (err) {
      console.error('Transactions error:', err);
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  const checkRegistrationStatus = async () => {
    try {
      const paid = await checkAuthStatus();
      setHasPaidRegistration(paid);
      if (paid) fetchPricing(parseInt(credits) || 0);
    } catch (err) {
      console.error('Error checking registration status:', err);
    }
  };

  const handleCreditsChange = (value) => {
    if (value === '') {
      setCredits('');
      return;
    }
    const num = parseInt(value);
    if (!isNaN(num) && num >= 0) {
      setCredits(num);
    }
  };

  const startPollingAfterPayment = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    let attempts = 0;
    pollingRef.current = setInterval(async () => {
      attempts++;
      try {
        await Promise.all([fetchBillingData(), fetchTransactions()]);
        if (attempts >= 10) clearInterval(pollingRef.current);
      } catch (err) {
        if (attempts >= 5) clearInterval(pollingRef.current);
      }
    }, 3000);
  };

  const handlePurchaseCredits = async () => {
    const numCredits = parseInt(credits) || 0;
    if (!phoneNumber) { setError('Enter your phone number'); return; }
    if (phoneNumber.length < 9) { setError('Enter a valid number e.g. 255712345678'); return; }
    if (numCredits < 1) { setError('Enter number of credits to purchase'); return; }
    if (!pricing) { setError('Please wait for pricing to load'); return; }

    setPurchasing(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/api/payments/credits/purchase', {
        credits: numCredits,
        msisdn: phoneNumber,
      });

      if (response.data.success) {
        setMessage(response.data.message || 'Payment initiated. Check your phone.');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 6000);
        startPollingAfterPayment();
      } else {
        setError(response.data.message || 'Payment failed. Try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleRegistrationPayment = async () => {
    if (!phoneNumber) { setError('Enter your phone number'); return; }
    if (phoneNumber.length < 9) { setError('Enter a valid number e.g. 255712345678'); return; }

    setPurchasing(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/api/payments/register', { msisdn: phoneNumber });

      if (response.data.success) {
        setMessage(response.data.message);

        let regAttempts = 0;
        const regInterval = setInterval(async () => {
          regAttempts++;
          try {
            const paid = await checkAuthStatus();
            if (paid) {
              clearInterval(regInterval);
              setHasPaidRegistration(true);
              setMessage('Account activated! You can now purchase credits.');
              setShowSuccess(true);
              fetchPricing(parseInt(credits) || 0);
              setTimeout(() => setShowSuccess(false), 5000);
            }
          } catch (err) {
            console.error('Registration poll error:', err);
          }
          if (regAttempts >= 20) clearInterval(regInterval);
        }, 3000);
      } else {
        setError(response.data.message || 'Payment failed. Try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong.');
    } finally {
      setPurchasing(false);
    }
  };

  // FIX 3: quick-select sets a real number (not empty string)
  const quickSelect = (amount) => setCredits(amount);

  const handleRefresh = async () => {
    await Promise.all([fetchBillingData(), fetchTransactions()]);
  };

  if (loading) {
    return (
      <div className="billing-loading">
        <div className="billing-spinner"></div>
        <p>Loading billing data…</p>
      </div>
    );
  }

  const numCredits = parseInt(credits) || 0;
  const totalPrice = pricing?.total ?? 0;
  const pricePerCredit = pricing?.pricePerCredit ?? 50;

  return (
    <div className="billing-page">

      {/* Toast */}
      {showSuccess && message && (
        <div className="billing-toast">
          <CheckCircle size={16} />
          <span>{message}</span>
          <button onClick={() => setShowSuccess(false)}><X size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div className="billing-hdr">
        <div>
          <h1>Billing & Credits</h1>
          <p>Manage your balance and top up credits</p>
        </div>
        <button className="refresh-btn" onClick={handleRefresh}>
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div className="billing-stats">
        <div className="stat-card green">
          <div className="stat-icon"><Wallet size={22} /></div>
          <div className="stat-body">
            <span className="stat-label">Credits Available</span>
            <span className="stat-value">{(stats?.creditBalance ?? 0).toLocaleString()}</span>
            <span className="stat-sub">≈ {(stats?.creditBalance ?? 0).toLocaleString()} messages</span>
          </div>
          <span className="stat-badge">Active</span>
        </div>

        <div className="stat-card blue">
          <div className="stat-icon"><MessageSquare size={22} /></div>
          <div className="stat-body">
            <span className="stat-label">Messages Sent</span>
            <span className="stat-value">{(stats?.totalMessagesSent ?? 0).toLocaleString()}</span>
            <span className="stat-sub">All time</span>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon"><TrendingUp size={22} /></div>
          <div className="stat-body">
            <span className="stat-label">Delivered</span>
            <span className="stat-value">{(stats?.totalMessagesDelivered ?? 0).toLocaleString()}</span>
            <span className="stat-sub">Successfully delivered</span>
          </div>
        </div>

        <div className="stat-card slate">
          <div className="stat-icon"><Zap size={22} /></div>
          <div className="stat-body">
            <span className="stat-label">Cost / Message</span>
            <span className="stat-value">TZS 50</span>
            <span className="stat-sub">1 credit = 1 message</span>
          </div>
        </div>
      </div>

      {/* Registration Banner */}
      {!hasPaidRegistration && registrationFee && (
        <div className="reg-banner">
          <div className="reg-banner-left">
            <Shield size={28} className="reg-icon" />
            <div>
              <h3>Activate your account</h3>
              <p>Pay a one-time fee of <strong>TZS {Number(registrationFee.amount).toLocaleString()}</strong> to unlock messaging.</p>
            </div>
          </div>
          <div className="reg-form">
            <input
              type="tel"
              placeholder="Phone number e.g. 255712345678"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={purchasing}
              className="reg-input"
            />
            <button
              onClick={handleRegistrationPayment}
              disabled={purchasing || !phoneNumber}
              className="reg-pay-btn"
            >
              {purchasing
                ? <><span className="btn-spinner" /> Processing…</>
                : <>Pay TZS {Number(registrationFee.amount).toLocaleString()}</>
              }
            </button>
          </div>
          <p className="reg-hint"><Smartphone size={13} /> You will get an STK Push on your phone</p>
        </div>
      )}

      {/* Purchase Section */}
      <div className="purchase-card">
        <div className="purchase-hdr">
          <div>
            <h2>Purchase Credits</h2>
            <p>1 credit = 1 WhatsApp message · TZS 50 per credit</p>
          </div>
        </div>

        {/* Quick Select */}
        <div className="quick-grid">
          {[100, 500, 1000, 2000, 5000, 10000].map((amt) => (
            <button
              key={amt}
              className={`quick-btn ${credits === amt ? 'active' : ''}`}
              onClick={() => quickSelect(amt)}
              disabled={!hasPaidRegistration || purchasing}
            >
              <span className="quick-credits">{amt.toLocaleString()}</span>
              <span className="quick-label">credits</span>
              <span className="quick-price">TZS {(amt * 50).toLocaleString()}</span>
            </button>
          ))}
        </div>

        {/* Custom + Phone + Price Row */}
        <div className="purchase-row">
          <div className="field-group">
            <label>Custom amount</label>
            <div className="input-with-unit">
              <input
                type="number"
                min="1"
                value={credits}
                onChange={(e) => handleCreditsChange(e.target.value)}
                disabled={!hasPaidRegistration || purchasing}
                className="field-input"
                placeholder="Enter credits"
              />
              <span className="input-unit">credits</span>
            </div>
          </div>

          <div className="field-group">
            <label>Phone number</label>
            <div className="input-with-unit">
              <Smartphone size={16} className="input-icon-left" />
              <input
                type="tel"
                placeholder="255712345678"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={!hasPaidRegistration || purchasing}
                className="field-input with-left-icon"
              />
            </div>
          </div>

          {/* Price Summary */}
          <div className="price-summary">
            <label>Price summary</label>
            <div className="price-box">
              {pricingLoading ? (
                <div className="price-loading">
                  <span className="btn-spinner dark small" /> Calculating…
                </div>
              ) : pricing ? (
                <>
                  <div className="price-row">
                    <span>{numCredits.toLocaleString()} credits</span>
                    <span>× TZS {Number(pricePerCredit).toLocaleString()}</span>
                  </div>
                  <div className="price-total">
                    <span>Total</span>
                    <strong>TZS {Number(totalPrice).toLocaleString()}</strong>
                  </div>
                </>
              ) : (
                <div className="price-empty">
                  {hasPaidRegistration ? 'Select credits to see price' : 'Activate account first'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="billing-alert error">
            <AlertCircle size={16} />
            <span>{error}</span>
            <button onClick={() => setError('')}><X size={14} /></button>
          </div>
        )}
        {message && !showSuccess && (
          <div className="billing-alert success">
            <CheckCircle size={16} />
            <span>{message}</span>
          </div>
        )}
        {!hasPaidRegistration && (
          <div className="billing-alert warning">
            <Shield size={16} />
            <span>Complete registration payment above before purchasing credits.</span>
          </div>
        )}

        {/* Buy Button */}
        <button
          className="buy-btn"
          onClick={handlePurchaseCredits}
          disabled={purchasing || !hasPaidRegistration || !phoneNumber || numCredits < 1 || !pricing || pricingLoading}
        >
          {purchasing ? (
            <><span className="btn-spinner" /> Processing payment…</>
          ) : (
            <>
              <CreditCard size={18} />
              Buy {numCredits > 0 ? numCredits.toLocaleString() : '—'} Credits
              {pricing ? ` · TZS ${Number(totalPrice).toLocaleString()}` : ''}
            </>
          )}
        </button>
      </div>

      {/* Transaction History */}
      <div className="tx-card">
        <div className="tx-hdr">
          <h3><Clock size={16} /> Transaction History</h3>
          <button className="refresh-btn small" onClick={fetchTransactions}>
            <RefreshCw size={13} />
          </button>
        </div>

        {txLoading ? (
          <div className="tx-empty">
            <div className="billing-spinner" style={{width:28,height:28,borderWidth:3}} />
            <p style={{marginTop:12}}>Loading transactions…</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="tx-empty">
            <CreditCard size={40} />
            <p>No transactions yet</p>
            <span>Credit purchases will appear here once completed</span>
          </div>
        ) : (
          <div className="tx-table-wrap">
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Type</th>
                  <th>Credits</th>
                  <th>Amount</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="tx-ref">{tx.sourceReference}</td>
                    <td>
                      <span className={`tx-type ${tx.transactionType === 'REGISTRATION' ? 'reg' : 'credit'}`}>
                        {tx.transactionType === 'REGISTRATION' ? 'Registration' : 'Credits'}
                      </span>
                    </td>
                    <td>{tx.credits != null ? tx.credits.toLocaleString() : '—'}</td>
                    <td className="tx-amount">TZS {Number(tx.amount).toLocaleString()}</td>
                    <td className="tx-channel">{tx.channel || tx.paymentMethod || '—'}</td>
                    <td>
                      <span className={`tx-status ${(tx.status || '').toLowerCase()}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="tx-date">
                      {tx.createdAt
                        ? new Date(tx.createdAt).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;